"use client";

import { c as _c } from "react/compiler-runtime";
import type { ComponentPropsWithoutRef, ElementType, ReactNode, Ref } from "react";
import { useInViewObservationTracking } from "@/vendors/Snowplow/hooks/use-in-view-observation-tracking.hook";
import { useMergedRefs } from "@/vendors/Snowplow/hooks/use-merged-refs.hook";
import type { BoundaryType } from "@/vendors/Snowplow/types/observation-boundary-tracker.types";
type ObservedBoundaryProps<T extends ElementType> = {
  readonly as?: T;
  readonly children: ReactNode;
  readonly boundaryId: string;
  readonly boundaryType: BoundaryType;
  readonly recommendationId?: string;
  readonly recommendationSignature?: string;
  readonly ref?: Ref<HTMLElement>;
  readonly position?: string;
} & Omit<ComponentPropsWithoutRef<T>, "as" | "children" | "boundaryId" | "boundaryType" | "recommendationId" | "recommendationSignature" | "position">;

/**
 * Polymorphic wrapper component that creates an observation boundary for tracking.
 *
 * Use this as a container around `ObservedElement` components to track visibility
 * metrics for all child elements within the boundary.
 *
 * When the boundary exits the viewport (or a lifecycle event occurs), tracking data
 * for all observed elements is sent to Snowplow.
 */
export function ObservedBoundary(t0) {
  const $ = _c(21);
  let as;
  let boundaryId;
  let boundaryType;
  let children;
  let externalRef;
  let position;
  let recommendationId;
  let recommendationSignature;
  let rest;
  if ($[0] !== t0) {
    ({
      as,
      children,
      boundaryId,
      boundaryType,
      recommendationId,
      recommendationSignature,
      position,
      ref: externalRef,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = as;
    $[2] = boundaryId;
    $[3] = boundaryType;
    $[4] = children;
    $[5] = externalRef;
    $[6] = position;
    $[7] = recommendationId;
    $[8] = recommendationSignature;
    $[9] = rest;
  } else {
    as = $[1];
    boundaryId = $[2];
    boundaryType = $[3];
    children = $[4];
    externalRef = $[5];
    position = $[6];
    recommendationId = $[7];
    recommendationSignature = $[8];
    rest = $[9];
  }
  let t1;
  if ($[10] !== boundaryId || $[11] !== boundaryType || $[12] !== recommendationId || $[13] !== recommendationSignature) {
    t1 = {
      boundaryId,
      boundaryType,
      recommendationId,
      recommendationSignature
    };
    $[10] = boundaryId;
    $[11] = boundaryType;
    $[12] = recommendationId;
    $[13] = recommendationSignature;
    $[14] = t1;
  } else {
    t1 = $[14];
  }
  const internalRef = useInViewObservationTracking(t1);
  const mergedRef = useMergedRefs(internalRef, externalRef);
  const Component = as ?? "section";
  let t2;
  if ($[15] !== Component || $[16] !== children || $[17] !== mergedRef || $[18] !== position || $[19] !== rest) {
    t2 = <Component {...rest} data-k5a-pos={position} ref={mergedRef}>{children}</Component>;
    $[15] = Component;
    $[16] = children;
    $[17] = mergedRef;
    $[18] = position;
    $[19] = rest;
    $[20] = t2;
  } else {
    t2 = $[20];
  }
  return t2;
}